export type NextFn = (err?: Error) => void;
export type Middleware<TContext> = (context: TContext) => (next: NextFn) => void;

type Pipeline<TContext> = {
  push: (...middlewares: Middleware<TContext>[]) => void;
  execute: (context: TContext) => Promise<void>;
};

/**
 * Creates a pipeline of middleware functions.
 *
 * @param initialMiddlewares The middleware functions to be executed in order.
 * @returns An object containing two functions:
 * - push: A function to add additional middleware functions to the pipeline.
 * - execute: A function to execute the pipeline of middleware functions.
 */
export function pipeline<TContext>(...initialMiddlewares: Middleware<TContext>[]): Pipeline<TContext> {
  const stack = initialMiddlewares;

  /**
   * Adds one or more middleware functions to the pipeline.
   *
   * @param middlewares - The middleware functions to be added to the pipeline.
   */
  const push = (...middlewares: Middleware<TContext>[]): void => {
    stack.push(...middlewares);
  };

  /**
   * Executes the pipeline of middleware functions.
   *
   * @async
   * @param context An object that contains the context for the middleware functions.
   * @throws {Error} If the next() function is called multiple times within the same middleware function.
   * @returns The return value of the last middleware function in the pipeline.
   */
  const execute = async (context: TContext): Promise<void> => {
    for (const middleware of stack) {
      // We want to run the middleware functions in order,
      // so we need to wait for each one to finish before moving on to the next.
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve, reject) => {
        let isCalled = false;
        try {
          middleware(context)((err) => {
            if (isCalled) {
              return;
            }
            isCalled = true;

            if (err) {
              reject(err);
              return;
            }

            resolve(true);
          });
        } catch (error_) {
          let error = new Error('An error occurred in the middleware function.');

          if (error_ instanceof Error) {
            error = error_;
          }

          reject(error);
        }
      });
    }
  };

  return { push, execute };
}
